import { LEAD_LIFECYCLE_LABELS, LEAD_TEMPERATURE_LABELS } from '@/lib/api/sales/leads-api'
import { SALES_FLOW_NODE_HANDLES, type SalesFlowGraphIssue, type SalesFlowNodeType, type SalesFlowRecipient } from '@mediardex/contracts'
import { Handle, NodeProps, Position } from '@xyflow/react'
import { MdErrorOutline, MdWarningAmber } from 'react-icons/md'
import { FLOW_GROUP_STYLE, FLOW_HANDLE_LABELS, FLOW_NODE_META, formatMinutes } from '../flow-meta'
import { findStage, type FlowRefData } from '../use-flow-ref-data'

export interface FlowCanvasNodeData {
    nodeKey: string
    type: SalesFlowNodeType
    config: Record<string, any>
    refData: FlowRefData
    issues: SalesFlowGraphIssue[]
    /** Quantas execuções passaram por aqui (fora simulação). */
    reached?: number
    [key: string]: unknown
}

const truncate = (text: string, size = 90) => (text.length > size ? `${text.slice(0, size - 1)}…` : text)

function userNames(ids: number[], refData: FlowRefData): string {
    const names = ids.map(id => refData.users.find(u => u.id === Number(id))?.name ?? `usuário ${id}`)
    return names.length > 2 ? `${names.slice(0, 2).join(', ')} e mais ${names.length - 2}` : names.join(' e ')
}

/** "Para Ana: " quando o envio não é para o lead. */
function recipientPrefix(recipient: SalesFlowRecipient | undefined, refData: FlowRefData): string {
    if (!recipient || recipient.kind === 'LEAD') return ''
    if (recipient.kind === 'ASSIGNED_USER') return 'Para o responsável: '
    if (recipient.kind === 'USERS') return (recipient.userIds ?? []).length ? `Para ${userNames(recipient.userIds!, refData)}: ` : 'Usuários não escolhidos: '
    const contacts = recipient.contacts ?? []
    if (!contacts.length) return 'Destinatários não informados: '
    return `Para ${contacts.length > 2 ? `${contacts.slice(0, 2).join(', ')} e mais ${contacts.length - 2}` : contacts.join(' e ')}: `
}

/** Uma linha que diz o que o passo faz, sem abrir o painel. */
export function summarizeNode(type: SalesFlowNodeType, config: Record<string, any>, refData: FlowRefData): string {
    const department = (id?: number | null) =>
        id ? refData.departments.find(d => d.id === Number(id))?.name ?? `setor ${id}` : 'setor da conversa'
    // Aviso para a equipe sem setor no passo não usa a conversa do lead.
    const teamDepartment = (config: Record<string, any>) =>
        !config.departmentId && config.recipient && config.recipient.kind !== 'LEAD' ? 'setor do fluxo ou de notificações' : department(config.departmentId)
    switch (type) {
        case 'START':
            return 'Quando um gatilho dispara'
        case 'SEND_WHATSAPP':
            return config.content ? `${recipientPrefix(config.recipient, refData)}${truncate(config.content)} · ${teamDepartment(config)}` : 'Mensagem não escrita'
        case 'SEND_TEMPLATE':
            return config.templateId ? `${recipientPrefix(config.recipient, refData)}Template · ${teamDepartment(config)}` : 'Template não escolhido'
        case 'SEND_EMAIL':
            return config.subject ? `${recipientPrefix(config.recipient, refData)}${truncate(config.subject)}` : 'Assunto não definido'
        case 'NOTIFY_USER': {
            const ids: number[] = config.userIds?.length ? config.userIds : config.userId ? [Number(config.userId)] : []
            return `Para ${ids.length ? userNames(ids, refData) : 'responsável pelo lead'}: ${truncate(config.message ?? '', 60)}`
        }
        case 'PAUSE':
            return `${formatMinutes(config.timeoutMinutes)}${config.waitForReply ? ' ou até o contato responder' : ''}`
        case 'CONDITION': {
            const count = (config.conditions ?? []).length
            return count ? `${count} ${count === 1 ? 'condição' : 'condições'}` : 'Sem condições (sempre sim)'
        }
        case 'GO_TO':
            return config.targetKey ? `Para o passo ${config.targetKey}` : 'Destino não escolhido'
        case 'MOVE_STAGE': {
            if (config.stageName != null) return config.stageName ? `"${config.stageName}" no funil atual` : 'Etapa não escolhida'
            const found = findStage(refData, config.stageId)
            return found ? `${found.pipeline.name} › ${found.stage.name}` : 'Etapa não escolhida'
        }
        case 'ASSIGN_USER': {
            if (config.mode === 'ROUND_ROBIN') {
                const count = (config.userIds ?? []).length
                return count ? `Rodízio entre ${count} ${count === 1 ? 'usuário' : 'usuários'}` : 'Rodízio sem usuários'
            }
            return refData.users.find(u => u.id === Number(config.userId))?.name ?? 'Usuário não escolhido'
        }
        case 'ASSIGN_WALLET':
            return refData.wallets.find(w => w.id === Number(config.walletId))?.name ?? 'Carteira não escolhida'
        case 'TAG':
            return config.tag ? `${config.mode === 'remove' ? 'Remover' : 'Adicionar'} "${config.tag}"` : 'Tag não definida'
        case 'SET_FIELD':
            return config.field ? `${config.entity === 'deal' ? 'Oportunidade' : 'Lead'}: ${config.field} = ${config.value ?? ''}` : 'Campo não escolhido'
        case 'SET_TEMPERATURE':
            return LEAD_TEMPERATURE_LABELS[config.temperature as keyof typeof LEAD_TEMPERATURE_LABELS] ?? 'Não escolhida'
        case 'SET_LIFECYCLE':
            return LEAD_LIFECYCLE_LABELS[config.lifecycleStatus as keyof typeof LEAD_LIFECYCLE_LABELS] ?? 'Não escolhido'
        case 'CREATE_DEAL': {
            const found = findStage(refData, config.stageId)
            return found ? `${found.pipeline.name} › ${found.stage.name}` : 'Funil não escolhido'
        }
        case 'CREATE_TASK': {
            if (!config.title) return 'Tarefa não descrita'
            const who = config.userId ? refData.users.find(u => u.id === Number(config.userId))?.name ?? 'usuário' : 'responsável pelo lead'
            return `${truncate(config.title, 50)} · ${who} · em ${formatMinutes(config.dueInMinutes ?? 60)}`
        }
        case 'ADD_NOTE':
            return config.content ? truncate(config.content) : 'Nota vazia'
        case 'WEBHOOK':
            return config.url ? `${config.method || 'POST'} ${truncate(config.url, 50)}` : 'URL não definida'
        case 'START_FLOW':
            return refData.flows.find(f => f.id === Number(config.flowId))?.name ?? 'Fluxo não escolhido'
        case 'STOP':
            return 'O fluxo termina aqui'
        default:
            return ''
    }
}

export default function FlowCanvasNode({ data, selected }: NodeProps) {
    const d = data as FlowCanvasNodeData
    const meta = FLOW_NODE_META[d.type]
    const style = FLOW_GROUP_STYLE[meta.group]
    const handles = SALES_FLOW_NODE_HANDLES[d.type]
    const errors = d.issues.filter(i => i.severity === 'error')
    const warnings = d.issues.filter(i => i.severity === 'warning')

    return (
        <div
            className={`w-64 rounded-xl border-2 bg-base-100 shadow-md transition-shadow ${style.ring} ${
                selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-base-200 shadow-xl' : ''
            } ${errors.length ? '!border-error' : ''}`}
        >
            {d.type !== 'START' && (
                <Handle type="target" position={Position.Left} className={`!h-3 !w-3 ${style.handle}`} />
            )}

            <div className={`flex items-center gap-2 rounded-t-[10px] px-3 py-2 ${style.header}`}>
                <span className="text-base">{meta.icon}</span>
                <span className="flex-1 truncate text-sm font-semibold">{meta.label}</span>
                {errors.length > 0 && <MdErrorOutline className="text-base" title={errors.map(e => e.message).join('\n')} />}
                {!errors.length && warnings.length > 0 && (
                    <MdWarningAmber className="text-base" title={warnings.map(w => w.message).join('\n')} />
                )}
            </div>

            <div className="px-3 py-2.5">
                <p className="text-xs leading-snug text-base-content/70 break-words">{summarizeNode(d.type, d.config, d.refData)}</p>
                {typeof d.reached === 'number' && d.reached > 0 && (
                    <p className="mt-1.5 text-[11px] font-medium text-base-content/50">
                        {d.reached.toLocaleString('pt-BR')} {d.reached === 1 ? 'execução passou' : 'execuções passaram'}
                    </p>
                )}
            </div>

            {handles.length > 0 && (
                <div className="border-t border-base-200 py-1">
                    {handles.map(handle => (
                        <div key={handle} className="relative flex h-7 items-center justify-end pr-4">
                            <span className={`text-[11px] ${handle === 'failed' ? 'text-error/80' : 'text-base-content/60'}`}>
                                {FLOW_HANDLE_LABELS[handle]}
                            </span>
                            <Handle
                                type="source"
                                id={handle}
                                position={Position.Right}
                                className={`!h-3 !w-3 ${handle === 'failed' ? '!bg-error' : style.handle}`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
